// The smart CPU. Differences from the basic one, each worth testing in the arena:
// - setup spots weigh scarce resources, number variety, the road (brick+lumber)
//   and city (ore+grain) pairs, and harbors it can use;
// - roads head for the best open spot up to three steps out;
// - it saves for whichever goal is fewest expected turns away, trades with the
//   bank and offers 1:1 (then 2:1) trades to players to get there;
// - it won't trade with anyone close to winning, and robs the leader;
// - it plays Knights to take Largest Army, and discards what its goal doesn't need.
// It only reads what a human in its seat could see: its own hand, card counts,
// the board, and public scores.

import { TERRAIN_RESOURCE } from './board';
import { best, missing, pips, production, robberOnMe } from './bot-common';
import { legalActions, validate } from './game';
import { COSTS, RESOURCES, hasResources, totalResources, type Resource, type Resources } from './resources';
import { MIN_LARGEST_ARMY, bankRate, canPlaceSettlement, discardOwed, knightsPlayed, publicVictoryPoints } from './rules';
import { topologyOf } from './topology';
import type { Action, GameState, TradeResponse } from './types';

type Goal = 'settlement' | 'city' | 'devCard' | 'road';

/** Offers per turn before it stops asking; keeps a table of humans from being pestered. */
const MAX_OFFERS = 2;

const zero = (): Resources => ({ brick: 0, lumber: 0, wool: 0, grain: 0, ore: 0 });

/** Rarer resources on this board are worth more (clamped so nothing is ignored or overvalued). */
function scarcity(state: GameState): Resources {
  const total = zero();
  for (const tile of state.board.tiles) {
    const r = TERRAIN_RESOURCE[tile.terrain];
    if (r) total[r] += pips(tile.token);
  }
  const avg = RESOURCES.reduce((n, r) => n + total[r], 0) / RESOURCES.length;
  const w = zero();
  for (const r of RESOURCES) w[r] = Math.min(1.6, Math.max(0.75, avg / Math.max(1, total[r])));
  return w;
}

function myTokens(state: GameState, player: number): Set<number> {
  const t = topologyOf(state);
  const out = new Set<number>();
  state.buildings.forEach((b, v) => {
    if (b?.owner !== player) return;
    for (const h of t.vertices[v]!.hexes) {
      const token = state.board.tiles[h]!.token;
      if (token !== null) out.add(token);
    }
  });
  return out;
}

/** How much `player` gains from a settlement at `v`. */
function spotValue(state: GameState, player: number, v: number, w = scarcity(state)): number {
  const t = topologyOf(state);
  const have = production(state, player);
  const tokens = myTokens(state, player);
  const here = zero();
  let score = 0;
  for (const h of t.vertices[v]!.hexes) {
    const tile = state.board.tiles[h]!;
    const r = TERRAIN_RESOURCE[tile.terrain];
    if (!r) continue;
    const p = pips(tile.token);
    here[r] += p;
    score += p * w[r] * (have[r] === 0 ? 1.4 : 1);
    if (tile.token !== null && tokens.has(tile.token)) score -= p * 0.25;
  }
  const has = (r: Resource) => have[r] + here[r] > 0;
  if (has('brick') && has('lumber')) score += 1.5;
  if (has('ore') && has('grain')) score += 1.5;
  for (const port of state.board.ports) {
    if (!t.edges[port.edge]!.vertices.includes(v)) continue;
    score += port.type === 'any' ? 1.2 : (have[port.type] + here[port.type]) * 0.3;
  }
  return score;
}

/** Open spots (empty, nobody adjacent) up to `depth` steps from `from`, walking only where others haven't built. */
function spotsNear(state: GameState, player: number, from: number, depth: number): { v: number; dist: number }[] {
  const t = topologyOf(state);
  const seen = new Map<number, number>([[from, 0]]);
  const queue = [from];
  while (queue.length) {
    const v = queue.shift()!;
    const d = seen.get(v)!;
    if (d >= depth) continue;
    for (const n of t.vertices[v]!.neighbors) {
      if (seen.has(n)) continue;
      const b = state.buildings[n];
      if (b && b.owner !== player) continue;
      seen.set(n, d + 1);
      queue.push(n);
    }
  }
  const out: { v: number; dist: number }[] = [];
  for (const [v, dist] of seen) {
    if (state.buildings[v] || t.vertices[v]!.neighbors.some((n) => state.buildings[n])) continue;
    out.push({ v, dist });
  }
  return out;
}

/** What a road along `edge` leads to: the best open spot within reach of its far end, discounted per extra road. */
function roadValue(state: GameState, player: number, edge: number, w = scarcity(state)): number {
  const t = topologyOf(state);
  let top = 0;
  for (const end of t.edges[edge]!.vertices) {
    const b = state.buildings[end];
    if (b?.owner === player) continue;
    // The end we already reach is the one touching our network; the other end is where the road goes.
    for (const { v, dist } of spotsNear(state, player, end, 2)) {
      top = Math.max(top, spotValue(state, player, v, w) * 0.7 ** dist);
    }
  }
  return top;
}

const isNearWin = (state: GameState, seat: number) => publicVictoryPoints(state, seat) >= state.config.victoryPointsToWin - 2;

/** Expected turns to collect `need`, from what the player produces (pips/36 per roll). */
function turnsFor(need: Resources, prod: Resources): number {
  let turns = 0;
  for (const r of RESOURCES) if (need[r] > 0) turns = Math.max(turns, need[r] / Math.max(0.5, (prod[r] / 36) * 1.0 + 0.08));
  return turns;
}

interface Plan {
  goals: Goal[];
  /** What the top goal still needs. */
  need: Resources;
  /** What the top goal will spend (the rest of the hand is surplus). */
  keep: Resources;
}

function plan(state: GameState, player: number): Plan {
  const p = state.players[player]!;
  const t = topologyOf(state);
  const prod = production(state, player);
  const hand = p.resources;
  const options: { goal: Goal; value: number }[] = [];
  const settlements = state.buildings.filter((b) => b?.owner === player && b.kind === 'settlement').length;
  if (p.citiesLeft > 0 && settlements > 0) options.push({ goal: 'city', value: 1.3 });
  const spot = p.settlementsLeft > 0 && t.vertices.some((_, v) => canPlaceSettlement(state, player, v, false));
  if (spot) options.push({ goal: 'settlement', value: 1.5 });
  if (state.devDeck.length > 0) {
    const holder = state.largestArmy;
    const armyClose = knightsPlayed(p) + 1 >= MIN_LARGEST_ARMY - 1 && holder !== player;
    options.push({ goal: 'devCard', value: armyClose ? 0.9 : 0.6 });
  }
  if (!spot && p.roadsLeft > 0 && p.settlementsLeft > 0) options.push({ goal: 'road', value: 0.8 });
  const scored = options.map((o) => ({ ...o, score: o.value / (1 + turnsFor(missing(hand, COSTS[o.goal]), prod)) }));
  scored.sort((a, b) => b.score - a.score);
  const goals = scored.map((o) => o.goal);
  const top = goals[0];
  return { goals, need: top ? missing(hand, COSTS[top]) : zero(), keep: top ? COSTS[top] : zero() };
}

function surplus(hand: Resources, keep: Resources): Resources {
  const out = zero();
  for (const r of RESOURCES) out[r] = Math.max(0, hand[r] - keep[r]);
  return out;
}

function legal(state: GameState, player: number, action: Action): Action | null {
  return validate(state, player, action) === null ? action : null;
}

export function smartAction(state: GameState, player: number): Action | null {
  const phase = state.phase;
  if (phase.kind === 'gameOver') return null;
  const me = state.players[player]!;
  const hand = me.resources;

  const owed = discardOwed(state, player);
  if (owed > 0) return { type: 'discard', resources: discardFor(state, player, owed) };

  if (phase.kind === 'main' && state.trade && state.trade.from !== player && state.trade.responses[player] === null) {
    return { type: 'respondTrade', accept: wantsTrade(state, player) };
  }

  if (player !== state.currentPlayer) return null;
  const actions = legalActions(state, player);
  if (actions.length === 0) return null;
  const of = <K extends Action['type']>(type: K) => actions.filter((a): a is Extract<Action, { type: K }> => a.type === type);
  const w = scarcity(state);

  switch (phase.kind) {
    case 'setup':
      if (phase.step === 'settlement') return best(of('buildSettlement'), (a) => spotValue(state, player, a.vertex, w)) ?? null;
      return best(of('buildRoad'), (a) => roadValue(state, player, a.edge, w)) ?? null;
    case 'roadBuilding':
      return best(of('buildRoad'), (a) => roadValue(state, player, a.edge, w)) ?? null;
    case 'moveRobber':
      return best(of('moveRobber'), (a) => robberValue(state, player, a.hex)) ?? null;
    case 'steal': {
      const victim = best(phase.candidates, (c) => publicVictoryPoints(state, c) * 3 + totalResources(state.players[c]!.resources));
      return victim === undefined ? null : { type: 'steal', victim };
    }
    case 'preRoll': {
      const knight = of('playKnight')[0];
      if (knight && (knightTakesArmy(state, player) || (robberOnMe(state, player) && pips(state.board.tiles[state.robber]!.token) >= 3))) return knight;
      return of('rollDice')[0] ?? null;
    }
    case 'discard':
      return null;
    case 'main':
      break;
  }

  if (state.trade?.from === player) return resolveOffer(state, player);

  const knight = of('playKnight')[0];
  if (knight && (knightTakesArmy(state, player) || robberOnMe(state, player))) return knight;

  // Points first: any affordable settlement or city is always worth taking.
  const settlement = best(of('buildSettlement'), (a) => spotValue(state, player, a.vertex, w));
  if (settlement) return settlement;
  const city = best(of('buildCity'), (a) => cityValue(state, a.vertex));
  if (city) return city;

  const { goals, need, keep } = plan(state, player);
  const goal = goals[0];
  const short = totalResources(need);

  const plenty = of('playYearOfPlenty');
  if (plenty.length > 0 && short > 0) {
    const wanted = RESOURCES.flatMap((r) => Array<Resource>(need[r]).fill(r));
    const pick: [Resource, Resource] = [wanted[0]!, wanted[1] ?? wanted[0]!];
    const match = legal(state, player, { type: 'playYearOfPlenty', resources: pick });
    if (match) return match;
  }
  const monopoly = of('playMonopoly');
  if (monopoly.length > 0 && short > 0) {
    // Guess from what opponents produce (public), not from their hidden hands.
    const theirs = zero();
    state.players.forEach((_, i) => {
      if (i === player) return;
      const prod = production(state, i);
      for (const r of RESOURCES) theirs[r] += prod[r];
    });
    const r = best(RESOURCES.filter((x) => need[x] > 0), (x) => theirs[x]);
    if (r) return legal(state, player, { type: 'playMonopoly', resource: r }) ?? of('endTurn')[0] ?? null;
  }

  if (goal === 'devCard' && short === 0) {
    const buy = of('buyDevCard')[0];
    if (buy) return buy;
  }
  if (goal === 'road') {
    // Nowhere to settle yet: the road toward the best open spot comes first.
    const road = best(of('buildRoad'), (a) => roadValue(state, player, a.edge, w));
    if (road && roadValue(state, player, road.edge, w) > 0) return road;
  }
  const roadCard = of('playRoadBuilding')[0];
  if (roadCard && goals.includes('road')) return roadCard;

  if (goal && short > 0) {
    const bank = bankTradeFor(state, player, need, keep);
    if (bank) return bank;
    if ((state.offersThisTurn ?? 0) < MAX_OFFERS && short <= 2) {
      const offer = offerFor(state, player, need, keep);
      if (offer) return offer;
    }
  }

  // A hand over 7 is a target for the next 7; spend it rather than sit on it.
  if (totalResources(hand) > 7) {
    const buy = of('buyDevCard')[0];
    if (buy) return buy;
    const road = best(of('buildRoad'), (a) => roadValue(state, player, a.edge, w));
    if (road) return road;
    const dump = bankTradeFor(state, player, goal ? need : zero(), keep, true);
    if (dump) return dump;
  }
  return of('endTurn')[0] ?? null;
}

function cityValue(state: GameState, v: number): number {
  const t = topologyOf(state);
  return t.vertices[v]!.hexes.reduce((n, h) => n + pips(state.board.tiles[h]!.token), 0);
}

function knightTakesArmy(state: GameState, player: number): boolean {
  const mine = knightsPlayed(state.players[player]!) + 1;
  if (mine < MIN_LARGEST_ARMY || state.largestArmy === player) return false;
  const holder = state.largestArmy;
  return holder === null || mine > knightsPlayed(state.players[holder]!);
}

/** Hurt the leader most, never ourselves. */
function robberValue(state: GameState, player: number, hex: number): number {
  const t = topologyOf(state);
  const p = pips(state.board.tiles[hex]!.token);
  const leader = best(
    state.players.map((_, i) => i).filter((i) => i !== player),
    (i) => publicVictoryPoints(state, i) * 10 + totalResources(state.players[i]!.resources),
  );
  let score = 0;
  for (const v of t.hexVertices[hex]!) {
    const b = state.buildings[v];
    if (!b) continue;
    if (b.owner === player) return -100;
    const weight = (b.kind === 'city' ? 2 : 1) * (b.owner === leader ? 2 : 1);
    score += p * weight + (totalResources(state.players[b.owner]!.resources) > 0 ? 1.5 : 0);
  }
  return score;
}

/** Discard what the goal doesn't need first, from the biggest piles. */
function discardFor(state: GameState, player: number, owed: number): Resources {
  const hand = state.players[player]!.resources;
  const { keep } = plan(state, player);
  const out = zero();
  let left = owed;
  const pass = (spare: (r: Resource) => number) => {
    while (left > 0) {
      const r = best(RESOURCES.filter((x) => spare(x) - out[x] > 0), (x) => spare(x) - out[x]);
      if (!r) return;
      out[r]++;
      left--;
    }
  };
  pass((r) => hand[r] - Math.min(hand[r], keep[r]));
  pass((r) => hand[r]);
  return out;
}

/** Trade the bank for something the goal needs, paying only from surplus. `dump` allows any useful-looking swap. */
function bankTradeFor(state: GameState, player: number, need: Resources, keep: Resources, dump = false): Action | null {
  const hand = state.players[player]!.resources;
  const spare = surplus(hand, keep);
  const receive = best(RESOURCES.filter((r) => need[r] > 0 || (dump && hand[r] === 0)), (r) => need[r] * 10 - hand[r]);
  if (!receive) return null;
  const give = best(
    RESOURCES.filter((r) => r !== receive && spare[r] >= bankRate(state, player, r)),
    (r) => spare[r] - bankRate(state, player, r),
  );
  return give ? legal(state, player, { type: 'bankTrade', give, receive }) : null;
}

/** 1 surplus card for 1 needed card; the second offer of a turn sweetens it to 2 for 1. */
function offerFor(state: GameState, player: number, need: Resources, keep: Resources): Action | null {
  const hand = state.players[player]!.resources;
  const spare = surplus(hand, keep);
  const want = best(RESOURCES.filter((r) => need[r] > 0), (r) => need[r]);
  const give = best(RESOURCES.filter((r) => r !== want && spare[r] > 0), (r) => spare[r]);
  if (!want || !give) return null;
  const count = (state.offersThisTurn ?? 0) >= 1 && spare[give] >= 2 ? 2 : 1;
  const offer = { type: 'offerTrade' as const, give: { ...zero(), [give]: count }, want: { ...zero(), [want]: 1 } };
  return legal(state, player, offer);
}

/** Our open offer: take the best acceptable answer, give up when everyone has answered, otherwise wait. */
function resolveOffer(state: GameState, player: number): Action | null {
  const trade = state.trade!;
  const { need, keep } = plan(state, player);
  const spare = surplus(state.players[player]!.resources, keep);
  const acceptable = (seat: number, r: TradeResponse | null): boolean => {
    if (!r || r.kind === 'decline' || isNearWin(state, seat)) return false;
    if (r.kind === 'accept') return true;
    // A counter: they give `r.give`, want `r.want`. Worth it if it brings what we need and costs only surplus.
    const helps = RESOURCES.some((x) => r.give[x] > 0 && need[x] > 0);
    const affordable = RESOURCES.every((x) => r.want[x] <= spare[x]);
    return helps && affordable && totalResources(r.want) <= totalResources(r.give) + 1;
  };
  const partner = best(
    trade.responses.flatMap((r, seat) => (seat !== player && acceptable(seat, r) ? [seat] : [])),
    (seat) => -publicVictoryPoints(state, seat),
  );
  if (partner !== undefined) {
    const confirm = legal(state, player, { type: 'confirmTrade', with: partner });
    if (confirm) return confirm;
  }
  const waiting = trade.responses.some((r, seat) => seat !== player && r === null);
  return waiting ? null : { type: 'cancelTrade' };
}

/** Say yes when it brings what we need, costs only surplus, and doesn't help someone about to win. */
function wantsTrade(state: GameState, player: number): boolean {
  const trade = state.trade!;
  const hand = state.players[player]!.resources;
  if (!hasResources(hand, trade.want) || isNearWin(state, trade.from)) return false;
  const { need, keep } = plan(state, player);
  const spare = surplus(hand, keep);
  const costsOnlySurplus = RESOURCES.every((r) => trade.want[r] <= spare[r]);
  if (!costsOnlySurplus) return false;
  const helps = RESOURCES.some((r) => trade.give[r] > 0 && need[r] > 0);
  const gets = totalResources(trade.give);
  const pays = totalResources(trade.want);
  return (helps && gets >= pays) || gets > pays;
}
